import { axios } from "utils";

import { PUBLIC_FARMS_URL as endpoint } from "consts";

import {
  AddFarmDetailsRequest,
  AddOrUpdateFarmDetailsResponse,
  BankingDetailsResponse,
  CooperationItemRequest,
  DataSubmissionDatesResponse,
  FarmDataSubmissionStatusResponse,
  FarmDetailsResponse,
  FarmFeatureFlagsResponse,
  FarmLivestockEntry,
  FarmOrganicFertilizerProductionEntry,
  FarmPaymentItemsResponse,
  FarmPaymentsResponse,
  FarmReportingYearResponse,
  FarmResponse,
  FarmSettings,
  GetFarmFeatureFlagsRequestBody,
  OwnProductionItemRequest,
  UpdateBankingDetailsRequest,
  UpdateFarmDetailsRequest,
} from "models";

// this file and endpoints should be refactored!
// every endpoint should have a prefix such as farm/field/shape/etc
// move base endpoint to constants

// farms

export const addFarm = async ({ farmDetails }: { farmDetails: AddFarmDetailsRequest }): Promise<AddOrUpdateFarmDetailsResponse> =>
  axios.post(`${endpoint}`, { data: farmDetails });

export const getFarms = async (): Promise<FarmResponse[]> => axios.get(`${endpoint}`);

export const getFarmDetails = async ({ farmId }: { farmId: string }): Promise<FarmDetailsResponse> => axios.get(`${endpoint}/${farmId}`);

export const updateFarmDetails = async ({ farmDetails }: { farmDetails: UpdateFarmDetailsRequest }): Promise<AddOrUpdateFarmDetailsResponse> =>
  axios.put(`${endpoint}/${farmDetails.id}`, { data: farmDetails });

export const deleteFarm = async ({ password, farmId }: { password: string; farmId: string }) => axios.delete(`${endpoint}/${farmId}`, { data: { password } });

// points and payment

export const getBankingDetails = async (data: { signal: AbortSignal }): Promise<BankingDetailsResponse> => axios.get(`${endpoint}/banking-details`, { data });

export const updateBankingDetails = async (data: UpdateBankingDetailsRequest): Promise<{ success: boolean }> =>
  axios.put(`${endpoint}/banking-details`, { data });

export const getFarmPayments = async (data: { signal: AbortSignal }): Promise<FarmPaymentsResponse> => axios.get(`${endpoint}/payments`, { data });

export const getFarmPaymentItems = async (): Promise<FarmPaymentItemsResponse[]> => axios.get(`${endpoint}/payment-items`);

export const generateCreditNote = async (data: { token: string; signal: AbortSignal }) => axios.get(`${endpoint}/credit-note/${data.token}`, { data });

export const getAllFarmManagementTools = async (data: { signal: AbortSignal }) => axios.get(`${endpoint}/farm-management-tools`, { data });

// data submission

export const addFarmDataSubmission = async (data: { farmId: string }): Promise<{ success: boolean; message: string }> =>
  axios.post(`${endpoint}/submissions`, { farmId: data.farmId });

export const getFarmDataSubmissionStatus = async (data: { farmId: string; signal: AbortSignal }): Promise<FarmDataSubmissionStatusResponse> =>
  axios.get(`${endpoint}/submissions/${data.farmId}`, { signal: data.signal });

export const getFarmReportingYear = async (): Promise<FarmReportingYearResponse> => axios.get(`${endpoint}/reporting-year`);

export const getDataSubmissionDates = async (): Promise<DataSubmissionDatesResponse> => axios.get(`${endpoint}/data-submission-dates`);

// feature flags

export const getFarmFeatureFlags = async (data: GetFarmFeatureFlagsRequestBody): Promise<FarmFeatureFlagsResponse[]> =>
  axios.get(`${endpoint}/feature-flags/${data.farmId}`);

// farm settings

export const getFarmSettings = async (): Promise<FarmSettings[]> => axios.get(`${endpoint}/settings`);

export const updateFarmSetting = async (data: { item: string; value: string }): Promise<{ success: boolean }> =>
  axios.put(`${endpoint}/settings`, { data: { item: data.item, value: data.value } });

// allotment

export const updateCreditAllotmentAcknowledgements = async (data: { farmId: string; creditAllotmentId: string }): Promise<{ success: boolean }> =>
  axios.put(`${endpoint}/${data.farmId}/credit-allotments/${data.creditAllotmentId}/acknowledgements`);

// livestock

export const getFarmLivestock = async (data: { farmId: string; year: number }): Promise<FarmLivestockEntry[]> =>
  axios.get(`${endpoint}/${data.farmId}/livestock`, { params: { year: data.year } });

export const createFarmLivestock = async (data: {
  farmId: string;
  year: number;
  livestockType: string;
  livestockAmount: number;
}): Promise<FarmLivestockEntry> =>
  axios.post(`${endpoint}/${data.farmId}/livestock`, { data: { year: data.year, livestockType: data.livestockType, livestockAmount: data.livestockAmount } });

export const updateFarmLivestock = async (data: {
  farmId: string;
  livestockId: string;
  livestockType: string;
  livestockAmount: number;
}): Promise<FarmLivestockEntry> =>
  axios.put(`${endpoint}/${data.farmId}/livestock/${data.livestockId}`, { data: { livestockType: data.livestockType, livestockAmount: data.livestockAmount } });

export const deleteFarmLivestock = async (data: { farmId: string; livestockId: string }): Promise<{ success: boolean }> =>
  axios.delete(`${endpoint}/${data.farmId}/livestock/${data.livestockId}`);

// organic fertilizer production

export const getFarmOrganicFertilizerProduction = async (data: { farmId: string; year: number }): Promise<FarmOrganicFertilizerProductionEntry[]> =>
  axios.get(`${endpoint}/${data.farmId}/organic-fertilizer-production`, { params: { year: data.year } });

export const createFarmOrganicFertilizerProduction = async (data: {
  farmId: string;
  year: number;
  fertilizerId: string;
  sourceType?: string | null;
  livestockCount?: number | null;
  comment?: string | null;
  ownProductionItems?: OwnProductionItemRequest[];
  cooperationItems?: CooperationItemRequest[];
}): Promise<FarmOrganicFertilizerProductionEntry> =>
  axios.post(`${endpoint}/${data.farmId}/organic-fertilizer-production`, {
    data: {
      year: data.year,
      fertilizerId: data.fertilizerId,
      sourceType: data.sourceType,
      livestockCount: data.livestockCount,
      comment: data.comment,
      ownProductionItems: data.ownProductionItems,
      cooperationItems: data.cooperationItems,
    },
  });

export const updateFarmOrganicFertilizerProduction = async (data: {
  farmId: string;
  productionId: string;
  year: number;
  fertilizerId?: string;
  sourceType?: string | null;
  livestockCount?: number | null;
  comment?: string | null;
  ownProductionItems?: OwnProductionItemRequest[];
  cooperationItems?: CooperationItemRequest[];
}): Promise<FarmOrganicFertilizerProductionEntry> =>
  axios.put(`${endpoint}/${data.farmId}/organic-fertilizer-production/${data.productionId}`, {
    data: {
      fertilizerId: data.fertilizerId,
      sourceType: data.sourceType,
      livestockCount: data.livestockCount,
      comment: data.comment,
      ownProductionItems: data.ownProductionItems,
      cooperationItems: data.cooperationItems,
    },
    params: { year: data.year },
  });

export const deleteFarmOrganicFertilizerProduction = async (data: { farmId: string; productionId: string; year: number }): Promise<{ success: boolean }> =>
  axios.delete(`${endpoint}/${data.farmId}/organic-fertilizer-production/${data.productionId}`, { params: { year: data.year } });
